import { useState, useEffect, memo } from 'react';
import { X, ShoppingCart, Star, Plus, Minus } from 'lucide-react';
import { Product } from '../types/supabase';
import { useCartStore } from '../store/cartStore';
import { formatEuro } from "../lib/utils";
import { productImage } from "../lib/productImage";
import { activeVariants, defaultVariant, hasDiscount } from "../lib/variants";
import VariantPicker from "./VariantPicker";

interface QuickViewProps {
  product: Product;
  onClose: () => void;
}

const QuickView = memo(function QuickView({ product, onClose }: QuickViewProps) {
  const variants = activeVariants(product);
  const [variantId, setVariantId] = useState<string | null>(() => defaultVariant(product)?.id ?? null);
  const variant = variants.find((v) => v.id === variantId) ?? null;
  const stock = variant?.stock ?? 0;

  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((state) => state.addItem);
  const cartItems = useCartStore((state) => state.items);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const cartItem = cartItems.find((item) => item.id === variantId);
  const isInCart = !!cartItem;

  // Cambiando formato si riparte dalla quantità già nel carrello per quel formato.
  useEffect(() => {
    setQuantity(cartItem?.quantity ?? 1);
  }, [variantId, cartItem?.quantity]);

  const handleAddToCart = () => {
    if (!variant) return;
    if (isInCart) {
      updateQuantity(variant.id, quantity);
    } else {
      addItem(product, variant, quantity);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90dvh] overflow-auto transform-gpu animate-modal-enter">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div className="self-start rounded-lg overflow-hidden">
            <img
              src={productImage(product.image_url)}
              alt={product.name}
              className="block w-full h-auto"
              loading="lazy"
              decoding="async"
            />
          </div>
          
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {product.name}
            </h2>
            
            <div className="flex items-center mb-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className="h-5 w-5 text-yellow-400 fill-current"
                  />
                ))}
              </div>
              <span className="ml-2 text-muted-foreground">
                (24 reviews)
              </span>
            </div>
            
            <p className="text-xl font-bold text-primary mb-4">
              {variant ? formatEuro(variant.price) : '—'}
              {variant && hasDiscount(variant) && (
                <span className="ml-2 text-base font-normal text-muted-foreground line-through">
                  {formatEuro(variant.original_price!)}
                </span>
              )}
            </p>

            {variants.length > 0 && (
              <div className="mb-6">
                <p className="text-foreground/80 mb-2 font-semibold">Formato</p>
                <VariantPicker
                  variants={variants}
                  selectedId={variantId}
                  onSelect={(v) => setVariantId(v.id)}
                />
              </div>
            )}
            
            <p className="text-muted-foreground mb-6 line-clamp-4">
              {product.description}
            </p>
            
            <div className="mb-6">
              <p className="text-foreground/80 mb-2">
                <span className="font-semibold">Categoria:</span> {product.category?.toUpperCase() || 'Nessuna Categoria'}
              </p>
              <p className="text-foreground/80 mb-2">
                <span className="font-semibold">Disponibilità:</span>{' '}
                {stock > 0 ? (
                  <span className="text-green-600 dark:text-green-400">Disponibile</span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">Non disponibili</span>
                )}
              </p>
            </div>
            
            <div className="flex items-center mb-6">
              <label htmlFor="quantity" className="mr-4 text-foreground/80">
                Quantità:
              </label>
              <div className="flex items-center border border-border rounded-md">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-1 text-muted-foreground hover:bg-muted transition-colors duration-200"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-4 py-1 text-foreground">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(stock, quantity + 1))}
                  className="px-3 py-1 text-muted-foreground hover:bg-muted transition-colors duration-200"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="mt-auto">
              <button
                onClick={handleAddToCart}
                disabled={stock === 0}
                className={`w-full flex items-center justify-center space-x-2 py-3 rounded-md transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                  stock > 0
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                    : 'bg-muted cursor-not-allowed text-muted-foreground'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>{stock > 0 ? (isInCart ? 'Aggiorna carrello' : 'Aggiungi al carrello') : 'Esaurito'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default QuickView;
